import { useState } from 'react';

import {
  Alert,
  Box,
  Card,
  IconButton,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import StepScreen from '@/karehero-app/access/StepScreen';
import { type Account } from '@/karehero-app/access/AccountBar';
import { INTAKE_SUMMARY } from '@/karehero-app/tell-us-once/intake/content';

/**
 * What we heard, read back before anything is asked of them.
 *
 * The review used to open on the facts list, which is the right screen for
 * changing one thing and the wrong one for answering "did you understand me".
 * This asks that question in the words they used, and the list waits behind it.
 *
 * One screen, two arrivals. Confirming leads on; saying it is not right leads
 * back into the conversation, and so does telling us again when the reading
 * failed. Either way the intake runs its processing again, so this screen comes
 * round a second time.
 */
const SummaryScreen = ({
  problem = false,
  editing: initialEditing = false,
  onConfirm,
  onCorrect,
  account,
  copy = INTAKE_SUMMARY,
}: {
  /** Arrived from the ping that says we could not read some of it. */
  problem?: boolean;
  /** Harness-only: open with the summary being edited. */
  editing?: boolean;
  onConfirm?: () => void;
  /** Back into the conversation, to say it again. */
  onCorrect?: () => void;
  /** The app bar. See StepScreen's own `account`. */
  account?: Account;
  /** The words. Onboarding 1.1 words the problem without blaming them. */
  copy?: typeof INTAKE_SUMMARY;
}) => {
  const [lines, setLines] = useState(problem ? copy.problemLines : copy.lines);
  // Edited in place, a field per paragraph, for a small fix that is quicker to
  // type than to explain. "That's not quite right" is still there for anything
  // bigger.
  const [editing, setEditing] = useState(initialEditing);

  return (
    <StepScreen
      account={account}
      title={problem ? copy.problemTitle : copy.title}
      primaryLabel={problem ? copy.keep : copy.confirm}
      isPrimaryDisabled={editing}
      onPrimary={() => onConfirm?.()}
      // The answer first, then the other one under it.
      footnoteBelow
      footnote={
        <Link
          component='button'
          type='button'
          variant='body2'
          onClick={() => onCorrect?.()}
        >
          {problem ? copy.tellAgain : copy.wrong}
        </Link>
      }
    >
      {/* On a card, and marked as ours rather than theirs. Loose on the page it
          read as the app talking in its own voice; in a card with the label on
          it, it reads as a thing we produced and are showing them, which is
          what they are being asked to check.

          The same sparkle and sunflower the fact list puts on a row we read out
          of what they sent, because this is the same claim about the same
          facts. */}
      <Stack spacing={2}>
        {/* An alert, not a line of description. Stock and unstyled, the way the
            chat's failures are: this has to read as something going wrong, and
            grey text under a heading reads as a caption. */}
        {problem && <Alert severity='error'>{copy.problem}</Alert>}

        <Card sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            <Stack sx={{ alignItems: 'center' }} direction='row' spacing={0.75}>
              <Box sx={{ color: 'custom.sunflower.700', display: 'flex' }}>
                <Icon icon={'sparkles' as never} fontSize={12} />
              </Box>
              <Typography
                sx={{ flex: 1, color: 'text.secondary' }}
                variant='overline'
              >
                {copy.label}
              </Typography>
              {!editing && (
                <IconButton
                  aria-label={copy.edit}
                  size='small'
                  onClick={() => setEditing(true)}
                >
                  <Icon icon={'pencil' as never} fontSize={14} />
                </IconButton>
              )}
            </Stack>

            {/* Paragraphs rather than one block: each line is a different thing
                we think we know, and they are scanned rather than read. */}
            {editing
              ? lines.map((line, i) => (
                  <TextField
                    key={i}
                    value={line}
                    onChange={(e) =>
                      setLines((prev) =>
                        prev.map((l, j) => (j === i ? e.target.value : l)),
                      )
                    }
                    multiline
                    fullWidth
                  />
                ))
              : lines.map((line, i) => (
                  <Typography key={i} variant='body1'>
                    {line}
                  </Typography>
                ))}

            {editing && (
              <Button
                variant='secondary'
                size='md'
                isFullWidth
                ariaLabel={copy.done}
                onPress={() => setEditing(false)}
              >
                {copy.done}
              </Button>
            )}
          </Stack>
        </Card>
      </Stack>
    </StepScreen>
  );
};

export default SummaryScreen;
